import {pointerUtils, type CreateExtArgs} from '@wix/document-manager-core'
import type {Pointer} from '@wix/document-services-types'
import _ from 'lodash'
import {COMP_DATA_QUERY_KEYS_WITH_STYLE, DATA_TYPES} from '../../../constants/constants'
import type {ComponentsMetadataAPI} from '../../componentsMetadata/componentsMetadata'
import type {DataModelExtensionAPI} from '../../dataModel/dataModel'
import type {MultilingualExtensionAPI} from '../../multilingual'
import type {RelationshipsAPI} from '../../relationships'
import type {StructureExtensionAPI} from '../../structure'
import type {VariantsExtensionAPI} from '../../variants/variants'

export const removeTranslationsForComponent = (createExtArgs: CreateExtArgs, compPtr: Pointer): void => {
    const {dal, pointers, extensionAPI} = createExtArgs
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    const removeTranslationsOfDataItem = (dataItemId: string, pageId: string) => {
        const mlApi = (extensionAPI as MultilingualExtensionAPI).multilingualTranslations
        if (!mlApi?.getLanguagesForItem) {
            return
        }
        const languages = mlApi.getLanguagesForItem(dataItemId)
        const {relationships} = extensionAPI as RelationshipsAPI
        for (const languageCode of languages) {
            const translatedPointer = pointers.multilingualTranslations.translationDataItem(
                pageId,
                languageCode,
                dataItemId
            )
            if (dal.has(translatedPointer)) {
                const ownedRefs = relationships.getOwnedReferredPointers(translatedPointer)
                for (const ref of ownedRefs) {
                    removeTranslationsOfDataItem(ref.id, pageId)
                    dataModel.removeItemRecursively(ref)
                }
            }
            dal.remove(translatedPointer)
        }
    }

    const dataQueryPointer = pointerUtils.getInnerPointer(compPtr, COMP_DATA_QUERY_KEYS_WITH_STYLE.data)
    const dataQuery = dal.get(dataQueryPointer)
    if (!dataQuery) {
        return
    }
    const dataItemId = _.trimStart(dataQuery, '#')
    const pagePointer = pointers.structure.getPageOfComponent(compPtr)
    if (pagePointer) {
        removeTranslationsOfDataItem(dataItemId, pagePointer.id)
    }
}

export const getComponentVariantPointers = (
    createExtArgs: CreateExtArgs,
    compPtr: Pointer,
    pageId?: string
): Pointer[] => {
    const {dal, pointers} = createExtArgs
    const componentVariantsData = pointers.data.getVariantDataItemsByComponentId(compPtr.id)
    const resolvedPageId = pageId || pointers.structure.getPageOfComponent(compPtr)?.id
    if (!resolvedPageId) {
        return []
    }
    return _.compact(
        _.map(componentVariantsData, (variantData: any) => {
            const variantPointer = pointers.data.getItem(DATA_TYPES.variants, variantData.id, resolvedPageId)
            return dal.has(variantPointer) ? variantPointer : null
        })
    )
}

export const removeScopedValuesReferencingVariants = (
    createExtArgs: CreateExtArgs,
    variantPointers: Pointer[]
): void => {
    if (variantPointers.length === 0) {
        return
    }
    const {variants: variantsApi} = createExtArgs.extensionAPI as VariantsExtensionAPI
    for (const variantPointer of variantPointers) {
        variantsApi.removeScopedValuesByVariants([variantPointer])
    }
}

export const removeComponentVariants = (createExtArgs: CreateExtArgs, variantPointers: Pointer[]): void => {
    const {dal} = createExtArgs
    for (const variantPointer of variantPointers) {
        if (dal.has(variantPointer)) {
            dal.remove(variantPointer)
        }
    }
}

export const deleteForkedMobileData = (createExtArgs: CreateExtArgs, desktopPointer: Pointer): void => {
    const {dal, pointers, extensionAPI} = createExtArgs
    const {mobile} = extensionAPI as StructureExtensionAPI
    const {componentsMetadata} = extensionAPI as ComponentsMetadataAPI
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    if (!mobile.isMobileNamespaceSupported()) {
        return
    }
    const mobilePointer = pointers.structure.getMobilePointer(desktopPointer)
    if (dal.has(mobilePointer)) {
        if (componentsMetadata.isMobileComponentPropertiesSplit(mobilePointer)) {
            dataModel.components.removeItem(mobilePointer, DATA_TYPES.prop)
        }
    }
}

export const removeFromParent = (
    createExtArgs: CreateExtArgs,
    parentPointer: Pointer | null,
    removedComponentId: string
) => {
    if (_.isNil(parentPointer)) {
        return
    }
    const {dal} = createExtArgs
    const componentsPtr = pointerUtils.getInnerPointer(parentPointer, 'components')
    const components = dal.get(componentsPtr)
    if (components) {
        dal.set(
            componentsPtr,
            components.filter((id: string) => id !== removedComponentId)
        )
    }
}
